import { VertexBuffer } from "./VertexBuffer";
import { BlendPass } from "./post/BlendPass";
import { IdResolvePass } from "./post/IdResolvePass";
import { MSAAResolvePass } from "./post/MSAAResolvePass";
import { MainPass } from "./main/MainPass";
import { GradientPass } from "./clear/GradientPass";
import { EnvMapPass } from "./clear/EnvMapPass";
import { SAOPass } from "./ssao/SAOPass";
import { GroundShadowPass } from "./ground/GroundShadowPass";
import { CommonRenderTargets, RequiredBytesPerSample } from "./CommonRenderTargets";
import { RenderBatchUniforms } from "./main/uniforms/RenderBatchUniforms";
import { getMaterialTextureMask, addMaterialTextureUpdateCallback, MaterialUniformFlags } from "./main/MaterialUniforms";
import { BufferGeometry } from "three";
import { getFallbackGeometry } from "./sceneToBatch";
import { BufferGeometryUtils } from "../../wgs/scene/BufferGeometry";
import { Model } from "../../application/Model";
import { TimingManager } from './TimingManager';
import { UsingTimestampQuery, GetGpuAdapterInfo } from "./globals";
import { trackWebGPUDeviceLost } from "../../analytics/index";
import { GPUCullingEngine } from "./gpudrivenrender/GPUCullingEngine";
import { GPUSceneRenderer } from "./gpudrivenrender/GPUSceneRenderer";
import { StateTrackingEncoder } from "./StateTrackingEncoder";
import { BufferManagerWebGPU } from "./BufferManagerWebGPU";
import { Megabytes, USE_WBOIT, DISABLE_STREAMING_DRAW } from "../globals";
import { setShaderExtensions } from "./main/modelMatrixHook";

const Events = {
  WEBGPU_DEVICE_LOST: 'webgpudevicelost',
  WEBGPU_INIT_FAILED: 'webgpuinitfailed',
  WEBGPU_RENDER_DONE: 'webgpurenderdone'
};


/**
 * @enum {Number}
 */
export const ToneMapMethod = {
  None: 0,
  CanonLum: 1,
  CanonRGB: 2
};


export function Renderer(device, params) {

  let _gpu;
  /** @type {GPUDevice} */
  let _device = device;
  let _canvas;
  let _initDone;
  let _pixelRatio;
  let _tonemapMethod = 0;
  let _envRotation = 0;
  const _envEncoding = { isGamma: true, isRGBM: false };

  /** @type {VertexBuffer} */
  let _vb;
  /** @type {GPUTextureFormat} */
  let _presentationFormat;
  /** @type {RenderBatchUniforms} */
  let _renderBatchUniforms;
  let _3dModels = new Set();
  let _3dModelBundlesInvalidated = new Map();
  let _3dModelVisibilityListener = new Map();

  let _renderTargets = new CommonRenderTargets(this);
  /** @type {GPUTexture|null} */
  let _offscreenTarget = null;
  let _mainPass = new MainPass(this);
  let _postPass = new BlendPass(this);
  let _idResolvePass = new IdResolvePass(this);
  let _msaaResolvePass = new MSAAResolvePass(this);
  let _gradientPass = new GradientPass(this);
  let _envMapPass = new EnvMapPass(this);
  let _sao = new SAOPass(this);
  let _groundShadowPass = new GroundShadowPass(this);

  /** @type {GPUSampler} */
  let _repeatSampler;
  /**
   * @typedef {Object} TextureInfo
   * @property {GPUTexture} texture
   * @property {GPUTextureView} view
   * @property {GPUSampler} sampler
   */
  /** @type {TextureInfo} Ugly pattern that can signal a missing texture. */
  let _missingTexture;
  /** @type {TextureInfo} */
  let _transparentTexture;

  _canvas = params.canvas || document.createElement("canvas");
  _gpu = _canvas.getContext("webgpu");
  _pixelRatio = params.pixelRatio;

  // Initialized by a call to setSize during initialization.
  let _viewportX = null;
  let _viewportY = null;
  let _viewportWidth = null;
  let _viewportHeight = null;

  // Used to allow temporary replacements of the viewport and recovering the original afterwards
  let _viewportStack = [];

  /** @type {DOMHighResTimeStamp} */
  let _timestamp = 0;
  /** @type {number} */
  let _loadingAnimationDuration = 0;

  let _materialTextureUpdateCallback = null;

  this.context = _gpu;

  /** @type {TimingManager|undefined} */
  let _timingManager;
  if (UsingTimestampQuery()) {
    _timingManager = new TimingManager(_device);
  }

  /** @type {GPUCullingEngine|null} */
  let _gpuCullingEngine = null;
  /** @type {BufferManagerWebGPU|null} */
  let _indirectBufferManager = null;

  /** @type {number} */
  let _fpsTarget = 30;
  /** @type {number} */
  let _targetSubmitsPerFrame = 10;
  /** @type {number} */
  let _batchesRenderedThisFrame = 0;

  Autodesk.Viewing.EventDispatcher.prototype.apply(this);

  // Register a material update callback that invalidates render bundles if the textures of a material changed.
  _materialTextureUpdateCallback = () => {
    this.invalidateRenderBundles();
  };
  addMaterialTextureUpdateCallback(_materialTextureUpdateCallback);

  // If the device is already available, we can initialize synchronously
  this.initSync = function (targetWidth, targetHeight) {

    _renderBatchUniforms = new RenderBatchUniforms(this);
    _presentationFormat = navigator.gpu.getPreferredCanvasFormat();

    // Note: While the WebGL renderer's context is configurable on construction via
    //       params.premultipliedAlpha, WebGPU always uses premultiplied.
    _gpu.configure({
      device: _device,
      format: _presentationFormat,
      alphaMode: "premultiplied"
    });

    _initDone = true;

    //Order matters below

    initGlobalTextures();

    _vb = new VertexBuffer(this);

    _renderTargets.init(false);
    _mainPass.init(_renderBatchUniforms);
    _sao.init();
    _postPass.init();
    _idResolvePass.init();
    _msaaResolvePass.init();
    _groundShadowPass.init(_renderBatchUniforms);

    this.setSize(targetWidth, targetHeight);

    _gradientPass.init();
    _envMapPass.init();

    if (GPUSceneRenderer.indirectDrawEnabled()) {
      _gpuCullingEngine = new GPUCullingEngine(this);

      const bufferDesc = {
        label: 'indirect draw call buffer',
        size: Megabytes(16),
        usage: GPUBufferUsage.STORAGE |
        GPUBufferUsage.COPY_DST |
        GPUBufferUsage.INDIRECT
      };
      _indirectBufferManager = new BufferManagerWebGPU(_device, bufferDesc);
    }

    _device.lost.then((info) => {
      console.error(`WebGPU device was lost: ${info.message}`);
      trackWebGPUDeviceLost(info.message);

      _device = null;

      // Please also note there is no "restore" event
      this.fireEvent({ type: Events.WEBGPU_DEVICE_LOST });
    });
  };

  function modelVisibilityDirtyCallback(model, renderer) {
    if (!this.visibilityDirty) {
      this.visibilityDirty = true;

      renderer.invalidateRenderBundles(model);
    }
  }


  /**
    * Reset dirty flag for model visibility.
    * @param {number} modelId
    */
  this.clear3dModelVisibilityDirty = function (modelId) {
    _3dModelVisibilityListener.get(modelId).visibilityDirty = false;
  };

  /**
   * The WebGPU renderer needs knowledge about 3D models for some features (e.g. render bundles, uniform batching).
   * @param {Model} model
   */
  this.add3dModel = function (model) {
    if (!model.is3d()) {
      console.warn("Called add3dModel with 2D model.");
      return;
    }

    _3dModels.add(model);
    _renderBatchUniforms.addModel(model);

    const listener = { visibilityDirty: false };
    const callback = modelVisibilityDirtyCallback.bind(listener, model, this);
    listener.callback = callback;

    // Not all types of models have a fragment list (e.g. Leaflets) and should therefore not be registered
    if (model.getFragmentList()) {
      model.getFragmentList().registerVisibilityDirtyCallback(callback);
    }
    _3dModelVisibilityListener.set(model.id, listener);
  };

  /**
   * Remove a model from the renderer. See add3dModel for details.
   * @param {Model} model
   */
  this.remove3dModel = function (model) {
    if (!model.is3d()) {
      console.warn("Called remove3dModel with 2D model.");
      return;
    }

    _3dModels.delete(model);
    _renderBatchUniforms.removeModel(model);

    const callback = _3dModelVisibilityListener.get(model.id).callback;
    if (model.getFragmentList()) {
      model.getFragmentList().removeVisibilityDirtyCallback(callback);
    }
    _3dModelVisibilityListener.delete(model.id);

    this.invalidateRenderBundles(model);

    // The ModelIteratorTexQuad does not implement getGeomScenes. Instead of using render batches
    // it uses the THREE.Scene. This means that the render batches are not invalidated and the render bundles
    // are not cleared. A possible solution would be a common interface for iterators that either implement
    // getGeomScenes and return render batches or basically return an empty array. This would allow us to remove
    // the if statement below.
    if (Object.hasOwn(model.getIterator(), 'getGeomScenes')) {
      // Reset render batch state
      const batches = model.getIterator().getGeomScenes();
      for (const batch of batches) {
        if (batch) {
          batch.isComplete = false;
          batch.useRenderBundles = false;
        }
      }
    }
  };

  /**
   * Whether this Renderer has this 3D model added.
   * @param {Model} model
   * @returns {boolean}
   */
  this.has3dModel = function (model) {
    return _3dModels.has(model);
  };

  function initGlobalTextures() {
    _repeatSampler = _device.createSampler({
      addressModeU: "repeat",
      addressModeV: "repeat"
    });

    initMissingTexture();
    _transparentTexture = createColorTexture(0x00000000);
  }

  /**
   * Create a 1x1 pixel texture of the given color.
   * @param {number} color A 4-byte color value in BGRA format (0xAARRGGBB).
   */
  function createColorTexture(color) {
    const texture = _device.createTexture({
      label: `color 0x${color.toString(16)}`,
      dimension: '2d',
      format: 'bgra8unorm',
      size: [1, 1],
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST
    });
    const view = texture.createView();

    const info = {
      texture,
      view,
      sampler: _repeatSampler
    };

    const data = new Uint32Array([color]);
    _device.queue.writeTexture(
      { texture }, data, { offset: 0, bytesPerRow: 4 }, [1, 1]);

    return info;
  }

  function initMissingTexture() {
    //Texture we bind to unused slots in bind groups that are reused with multiple configurations
    let texture = _device.createTexture({
      label: 'missing',
      dimension: "2d",
      format: "bgra8unorm",
      size: [4, 4],
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST
    });

    let view = texture.createView();

    _missingTexture = {
      texture,
      view,
      sampler: _repeatSampler
    };

    // Winner of most annoying looking texture in the world for 2016
    const data = new Uint32Array(16);
    const w = 0xffffffff;const r = 0xffff0000;
    data[0] = r;data[1] = w;data[2] = w;data[3] = r;
    data[4] = r;data[5] = w;data[6] = r;data[7] = r;
    data[8] = r;data[9] = r;data[10] = w;data[11] = r;
    data[12] = w;data[13] = w;data[14] = r;data[15] = w;

    _device.queue.writeTexture({ texture },
    data,
    { offset: 0, bytesPerRow: 16 },
    [4, 4]
    );
  }

  /** @returns {RenderBatchUniforms} */
  this.getRenderBatchUniforms = function () {
    return _renderBatchUniforms;
  };

  // --- Shader Extensions API ---
  /** @type {import('./ShaderExtension').ShaderExtension[]} */
  const _shaderExtensions = [];

  // Tracks the next free binding index per bind group for ShaderExtension allocation.
  // Group 1 starts after core bindings (0–3).
  const _nextBinding = { 1: 4 };

  function _initShaderExtension(extension) {
    extension._acquireBinding = (group) => {
      if (!(group in _nextBinding)) {
        _nextBinding[group] = 0;
      }
      return _nextBinding[group]++;
    };
    extension.init(_device);
  }

  /**
   * Registers a ShaderExtension that customizes shader behavior (e.g. model matrix override).
   * Triggers pipeline rebuild since shader code changes.
   * @param {import('./ShaderExtension').ShaderExtension} extension
   */
  this.addShaderExtension = function (extension) {
    if (_shaderExtensions.indexOf(extension) !== -1) {
      return;
    }
    _shaderExtensions.push(extension);
    _initShaderExtension(extension);
    setShaderExtensions(_shaderExtensions);
    _renderBatchUniforms.setShaderExtensions(_shaderExtensions);
    _mainPass.resetPipelines();
    _groundShadowPass.resetPipelines();
    this.invalidateRenderBundles();
  };

  /**
   * Removes a previously registered ShaderExtension.
   * Triggers pipeline rebuild since shader code changes.
   * @param {import('./ShaderExtension').ShaderExtension} extension
   */
  this.removeShaderExtension = function (extension) {
    const idx = _shaderExtensions.indexOf(extension);
    if (idx === -1) {
      return;
    }
    extension.dispose();
    _shaderExtensions.splice(idx, 1);
    setShaderExtensions(_shaderExtensions);
    _renderBatchUniforms.setShaderExtensions(_shaderExtensions);
    _mainPass.resetPipelines();
    _groundShadowPass.resetPipelines();
    this.invalidateRenderBundles();
  };

  /**
   * Called by extension users when an extension's per-batch upload state changes
   * (e.g. animation starts/stops). Adjusts submission strategy and invalidates render bundles.
   */
  this.notifyExtensionStateChanged = function () {
    if (_renderBatchUniforms.anyExtensionNeedsPerBatchUpload()) {
      _mainPass.setMaxBatchesPerSubmit(1);
    }
    this.invalidateRenderBundles();
  };

  /**
   * Set edge color for main (non-ghosted) edges.
   * @param {{ x: number, y: number, z: number, w: number }} colorAsVec4 - RGBA in [0,1]
   */
  this.setEdgeColor = function (colorAsVec4) {
    _mainPass.setEdgeColor(colorAsVec4);
  };

  /**
   * Set edge color for ghosted edges.
   * @param {{ x: number, y: number, z: number, w: number }} colorAsVec4 - RGBA in [0,1]
   */
  this.setEdgeColorGhosted = function (colorAsVec4) {
    _mainPass.setEdgeColorGhosted(colorAsVec4);
  };

  /**
   * Set whether dimmed (ghosted) edges use dithering. Forwards to MainPass.
   * @param {boolean} [dithered=false] - true = checker dither, false = solid.
   */
  this.setDitheredGhosting = function (dithered) {
    _mainPass.setDitheredGhosting(dithered);
  };

  /**
   * Fragments with alpha >= this threshold are considered mostly opaque and write to the ND-buffer (normal/depth buffer for SSAO).
   * Required by Tandem-renderer.
   * @param {number} threshold - Opacity threshold (0.0 to 1.0). Default: 0.9
   */
  this.setNdBufferOpacityThreshold = function (threshold) {
    _renderBatchUniforms.getCommonMaterialUniforms().setNdBufferOpacityThreshold(threshold);
  };

  this.getPixelRatio = function () {
    return _pixelRatio || window?.devicePixelRatio || 1;
  };
  this.setPixelRatio = function (value) {
    _pixelRatio = value;
  };

  this.setSize = function (width, height, updateStyle) {
    width = width ?? 0;
    height = height ?? 0;

    _canvas.width = width * this.getPixelRatio();
    _canvas.height = height * this.getPixelRatio();

    if (updateStyle !== false) {

      _canvas.style.width = width + 'px';
      _canvas.style.height = height + 'px';
    }

    //This one needs to be first
    _renderTargets.resize(_canvas.width, _canvas.height);

    if (_canvas.width !== 0 && _canvas.height !== 0) {
      // These passes depend on textures that no longer exist when
      // resized to 0.
      _mainPass.resize(_canvas.width, _canvas.height);
      _sao.resize(_canvas.width, _canvas.height);
      _postPass.resize();
      _idResolvePass.resize();
      _msaaResolvePass.resize();
    }

    this.setViewport(0, 0, width, height);
  };

  /**
   * @param {ToneMapMethod} value
   */
  this.setTonemapMethod = function (value) {
    if (_tonemapMethod !== value) {
      _tonemapMethod = value;
      _mainPass.resetPipelines();
      this.invalidateRenderBundles();
    }
  };

  this.getTonemapMethod = function () {
    return _tonemapMethod;
  };

  this.setEnvMapEncoding = function (map) {
    // If no map is provided, just assume both flags as false.
    // Without an envMap, they don't have an effect anyway.
    const isGamma = map ? !!map.GammaEncoded : false;
    const isRGBM = map ? !!map?.RGBM : false;
    if (_envEncoding.isGamma !== isGamma || _envEncoding.isRGBM !== isRGBM) {
      _envEncoding.isGamma = isGamma,
      _envEncoding.isRGBM = isRGBM;
      _mainPass.resetPipelines();
      this.invalidateRenderBundles();
    }
  };

  this.getEnvMapEncoding = function () {
    return _envEncoding;
  };

  this.setEnvRotation = function (rotation) {
    if (_envRotation !== rotation) {
      _envRotation = rotation;
      _envMapPass.setEnvRotation(rotation);
      _mainPass.getIBL().setEnvRotation(rotation);
    }
  };

  this.getEnvRotation = function () {
    return _envRotation;
  };

  /**
   * Returns the current MSAA sample count.
   * @returns {number} 1 = no MSAA, 4 = 4x MSAA
   */
  this.getMSAASampleCount = function () {
    return _renderTargets.getSampleCount();
  };

  /**
   * Sets the MSAA sample count. Recreates render targets and pipelines.
   * @param {number} sampleCount - 1 for no MSAA, 4 for 4x MSAA
   */
  this.setMSAASampleCount = function (sampleCount) {
    if (sampleCount !== 1 && sampleCount !== 4) {
      console.warn(`Unsupported MSAA sample count: ${sampleCount}. Must be 1 or 4.`);
      return;
    }

    if (_renderTargets.getSampleCount() === sampleCount) return;

    const [w, h] = _renderTargets.getTargetSize();
    _renderTargets.setSampleCount(sampleCount);

    if (w > 0 && h > 0) {
      _renderTargets.resize(w, h);
      _mainPass.resize(w, h);
      _sao.invalidate();
      _sao.resize(w, h);
      _postPass.resize();
      _idResolvePass.resize();
      _msaaResolvePass.resize();
    }

    _mainPass.resetPipelines();
    this.invalidateRenderBundles();
    _gradientPass.resetPipeline();
    _envMapPass.resetPipeline();
    _groundShadowPass.resetPipelines();
  };

  /** @returns {boolean} Whether MSAA is currently enabled. */
  this.isMSAAEnabled = function () {
    return _renderTargets.getSampleCount() > 1;
  };

  /**
   * Resolves multisampled render targets to single-sampled textures.
   * Must be called after all main rendering is complete and before any
   * post-processing passes that read from the render targets.
   */
  this.resolveMSAATargets = function () {
    const commandEncoder = _device.createCommandEncoder({ label: 'msaa-resolve-encoder' });
    _msaaResolvePass.run(commandEncoder);
    _idResolvePass.run(commandEncoder);
    _device.queue.submit([commandEncoder.finish()]);
  };

  this.renderBackground = function (useEnvMap) {

    if (useEnvMap && _envMapPass.hasCubeMap()) {
      _envMapPass.run();
    } else {
      _gradientPass.run();
    }

  };

  this._updateBatchHeuristic = function () {
    // When any extension needs per-batch upload, keep maxBatchesPerSubmit = 1
    // so each batch's writeBuffer is consumed before the next overwrites the shared buffer.
    const needsPerBatch = _renderBatchUniforms.anyExtensionNeedsPerBatchUpload();
    if (!needsPerBatch) {
      let batchesPerSubmit = Math.round(
        _batchesRenderedThisFrame / _targetSubmitsPerFrame);
      batchesPerSubmit = Math.max(1, batchesPerSubmit);
      _mainPass.setMaxBatchesPerSubmit(batchesPerSubmit);
    }

    _batchesRenderedThisFrame = 0;
  };

  this.present = function (antialias, camera, waitForDone, userFinalPass = null) {
    // Make sure all MainPass commands are submitted.
    this.flushCommandQueue();

    this._updateBatchHeuristic();

    if (!_initDone) {
      this.fireEvent({ type: Events.WEBGPU_RENDER_DONE });
      return;
    }

    let finalTarget = _offscreenTarget || _gpu.getCurrentTexture();

    // MSAA already provides edge antialiasing; running FXAA on top would be redundant
    // (and noticeably softens the image). Force-disable FXAA when MSAA is active.
    const useFxaa = antialias && !this.isMSAAEnabled();

    if (!userFinalPass) {
      // Default way: PostPass => finalTarget
      _postPass.run(finalTarget.createView(), useFxaa, camera);
    } else {
      // render blendPass into post1
      const post1 = _renderTargets.getPostTarget(1);
      _postPass.run(post1.createView(), useFxaa, camera);

      // render userFinalPass from post1 into finalTarget
      userFinalPass.run(finalTarget, post1);
    }

    if (waitForDone) {
      // Inform listeners as soon as all currently submitted commands are done.
      _device.queue.onSubmittedWorkDone().then(() => {
        this.fireEvent({ type: Events.WEBGPU_RENDER_DONE });
      });
    }
  };

  this.beginScene = function (camera, lights) {

    if (!_initDone) return;

    _3dModelBundlesInvalidated.clear();

    _mainPass.beginScene(camera, lights);
  };

  //TODO: needClear and updateLights are bogus
  this.renderScenePart = function (scene, showEdges, renderToOverlay = false) {
    if (!_initDone) return;

    _mainPass.renderScenePart(scene, showEdges, renderToOverlay);
    _batchesRenderedThisFrame++;
  };

  this.renderOverlayModels = function (models, camera, showEdges, lights) {
    if (!_initDone) return;

    _mainPass.beginScene(camera, lights);
    for (const model of models) {
      // Get all render batches for the model
      const batches = model.getIterator().getGeomScenes();

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        // Batches bypass nextBatch(), so applyVisibility() never sets MESH_RENDERFLAG.
        // Fall back to MESH_VISIBLE to avoid rendering nothing.
        batch.forceVisible = true;
        this.renderScenePart(batch, showEdges, true);
      }
    }
  };

  this.renderOverlay = function (scene, camera, materialPre, materialPost, showEdges, customEdgeColor, lights) {

    if (!_initDone) return;

    _mainPass.renderOverlay(scene, camera, materialPre, materialPost, showEdges, customEdgeColor, lights);
    _batchesRenderedThisFrame++;
  };

  this.flushCommandQueue = function (flushTransparents = true) {
    _mainPass.submitCommands();
    if (flushTransparents) {
      _mainPass.submitWboitCommands();
    }
  };

  this.afterTick = function () {
    _vb.cleanup();

    if (_timingManager) {
      _timingManager.resolveQueries();
      _timingManager.readQueries();
      // _timingManager.printTimes();
    }
  };

  /**
   * @returns {GPUDevice}
   */
  this.getDevice = function () {
    return _device;
  };

  /** @return {CommonRenderTargets} */
  this.getRenderTargets = function () {
    return _renderTargets;
  };

  /** @returns {VertexBuffer} */
  this.getVB = function () {
    return _vb;
  };

  this.getGradientPass = function () {
    return _gradientPass;
  };

  this.getEnvMapPass = function () {
    return _envMapPass;
  };

  this.getIBL = function () {
    return _mainPass.getIBL();
  };

  this.getSAO = function () {
    return _sao;
  };

  this.getMainPass = function () {
    return _mainPass;
  };

  this.getGroundShadowPass = function () {
    return _groundShadowPass;
  };

  this.getPostPass = function () {
    return _postPass;
  };

  this.getBlendSettings = function () {
    return _postPass.getBlendSettings();
  };

  this.setRenderTarget = function (target) {

    //console.log("deprecated setRenderTarget");
  };
  /**
   * @returns {GPUTexture|null}
   */
  this.getOffscreenTarget = function () {
    return _offscreenTarget;
  };

  /**
   * @param {GPUTexture|null} target
   */
  this.setOffscreenTarget = function (target) {
    _offscreenTarget = target;
  };

  this.clearTarget = function (target) {

    //TODO: this is to be removed
  };this.clearMainTargets = function () {
    //TODO: we really only needs this clear if there is no initial scene to draw
    //from the beginScene() call of RenderContext, otherwise it can be
    //done via implicit clear with that scene
    _initDone && _mainPass.clearMainTargets();
  };
  this.clearOverlayTargets = function () {
    //TODO: we only need this explicit clear if there aren't
    //any overlays to draw -- otherwise the implicit clear
    //during the pass that draws the overlays can do it.
    _initDone && _mainPass.clearOverlayTargets();
  };

  this.clearMainDepth = function () {
    _initDone && _mainPass.clearMainDepth();
  };

  this.copyDepthToOverlay = function () {
    _initDone && _mainPass.copyDepthToOverlay();
  };

  this.clearWboitTargets = function () {
    _mainPass.clearWboitTargets();
  };

  this.setSkipIdDepthCopy = function (skip) {
    _mainPass.setSkipIdDepthCopy(skip);
  };

  this.clear = function () {

  };
  this.depthFunc = function () {

  };

  /** @returns {DOMHighResTimeStamp} */
  this.getTimestamp = function () {
    return _timestamp;
  };

  /**
   * @param {DOMHighResTimeStamp} timestamp
   * @returns {DOMHighResTimeStamp}
  */
  this.updateTimestamp = function (timestamp) {
    _timestamp = timestamp;
    return _timestamp;
  };

  /** @returns {number} */
  this.getLoadingAnimationDuration = function () {
    return _loadingAnimationDuration;
  };

  /**
   * @param {number} duration
   * @returns {number}
  */
  this.setLoadingAnimationDuration = function (duration) {
    _loadingAnimationDuration = duration;
    return _loadingAnimationDuration;
  };

  this.getMaxAnisotropy = function () {
    return 16;
  };

  this.supportsMRT = function () {return true;};
  this.verifyMRTWorks = function () {return true;};

  this.cleanup = function () {
    _renderBatchUniforms?.dtor();
    _renderBatchUniforms = null;
    //TODO: destroy all targets/buffers
    //destroy passes that own targets/other resources
  };

  /** @returns {TextureInfo} A 1x1 transparent texture */
  this.getPlaceholderTexture = function () {
    return _transparentTexture;
  };

  this.stats = function () {
    _vb.stats();
  };

  this.setLineStyleBuffer = function (buffer, width) {
    _mainPass.setLineStyleBuffer(buffer, width);
  };

  this.invalidateRenderBundles = function (model) {
    let models;
    if (model) {
      models = [model];
    } else {
      models = _3dModels;
    }

    models.forEach((model) => {
      if (!_3dModelBundlesInvalidated.has(model.id)) {
        const iter = model.getIterator();
        if (iter.getGeomScenes) {
          const scenes = model.getIterator().getGeomScenes();
          for (const scene of scenes) {
            if (scene) {
              _mainPass.clearRenderBundles(scene);
            }
          }
        }
        _3dModelBundlesInvalidated.set(model.id);
      }
    });
  };

  this.getContext = function () {
    return this.context;
  };

  /**
   * @param {number} size
   * @returns {boolean} Whether or not size bytes can be uploaded this frame.
   */
  this.canUpload = function (size) {
    return _vb.canUpload(size);
  };

  /**
   * Uploads a new geometry to the GPU.
   * @param {BufferGeometry} geometry
   */
  this.uploadGeometry = function (geometry) {
    _vb.initUploaded(geometry);
  };

  /**
   * Upload range of uniforms for the given scene. Pre-uploading the uniform speeds-up rendering,
   * but requires to be done again whenever the order or count changes.
   * @param {RenderBatch} scene
   */
  this.uploadUniforms = function (scene) {
    _renderBatchUniforms.updateBatch(scene);
  };

  /**
   * Free GPUMemory that was previously allocated by uploading uniforms for the given scene.
   * @param {RenderBatch} scene
   */
  this.freeUniforms = function (scene) {
    _renderBatchUniforms.freeUniforms(scene);
  };

  /**
   * The total memory used by this Renderer.
   * @returns {number}
   */
  this.getTotalMemory = function () {
    // TODO: Add ObjectUniforms and any other GPU memory users.
    return _vb.getTotalMemory();
  };

  /**
   * Returns a valid geometry if it can be drawn, or null if it can't be drawn.
   * This may modify the geometry passed in or return a different object that is
   * drawable.
   * @param {THREE.Geometry|BufferGeometry} geometry
   * @return {BufferGeometry|null}
   */
  this.initGeometry = function (geometry) {
    // THREE.Geometry is not supported by the WebGPU renderer and needs to be converted
    // before use. Possible usages include overlay scenes, the pivot (SphereGeometry)
    // and hypermodel gizmo (PlaneGeometry).
    if (geometry instanceof THREE.Geometry) {
      geometry = getFallbackGeometry(geometry);
      if (!geometry) {
        return null;
      }
      if (!DISABLE_STREAMING_DRAW) {
        geometry.streamingDraw = true;
      }
    }

    // HACK: non-interleaved geometries need re-interleaving when their vertex buffer is updated.
    //       See same hack in CustomShaderPipeline.drawOne() for more.
    //       In case DISABLE_STREAMING_DRAW is set you need to call geometry.dispose() in addition to
    //       vbNeedsUpdate = true (reset to false by interleaveGeometry) for the vertex buffer to be uploaded.
    const isNonInterleaved = geometry.attributes.position.array;
    if (!geometry.vb || geometry.vbNeedsUpdate && isNonInterleaved) {
      BufferGeometryUtils.interleaveGeometry(geometry, true);
      if (!DISABLE_STREAMING_DRAW) {
        geometry.streamingDraw = true;
      }
    }

    if (!_vb.canDraw(geometry)) {
      return null;
    }

    return geometry;
  };

  /**
   * Sets the viewport for rendering. Use passViewport(encoder) later to actually use the viewport.
   * @Note The position is in WebGL coordinates, a y-flip is perfomed internally using the canvas heigth.
   *       This implies the current implementation can only set viewports in passes where the render
   *       attachments share the same size as the canvas!
   *
   * @Note By default WebGL only applies the viewport when rendering to the canvas unless setting
   *       enableViewportOnOffscreenTargets(). WebGPU does not have this options and always renders
   *       to offscreen targets. Viewport support is incomplete as of now.
   *
   * @see https://jira.autodesk.com/browse/VIZX-1879 for remaining viewport issues.
   */
  this.setViewport = function (glX, glY, width, height) {
    const pixelRatio = this.getPixelRatio();
    _viewportX = Math.trunc(glX * pixelRatio);
    // (x=0, y=0) is bottom-left in WebGL but top-left in WebGPU, so we need to flip y.
    _viewportY = _canvas.height - Math.trunc((glY + height) * pixelRatio);

    _viewportWidth = Math.trunc(width * pixelRatio);
    _viewportHeight = Math.trunc(height * pixelRatio);

    this._viewportChanged();
  };

  /**
   * Pass the current viewport to a GPURenderPassEncoder.
   * @param {GPURenderPassEncoder|StateTrackingEncoder} passEncoder
   */
  this.passViewport = function (passEncoder) {
    passEncoder.setViewport(_viewportX, _viewportY, _viewportWidth, _viewportHeight, 0, 1);
  };

  /** Push current viewport to viewport stack, so that it can be recovered by popViewport later. */
  this.pushViewport = function () {
    _viewportStack.push(_viewportX);
    _viewportStack.push(_viewportY);
    _viewportStack.push(_viewportWidth);
    _viewportStack.push(_viewportHeight);
  };

  /** Recover previously pushed viewport. */
  this.popViewport = function () {
    if (_viewportStack.length == 0) {
      console.warn("Called popViewport on empty stack");
      return;
    }

    const index = _viewportStack.length - 4;
    _viewportX = _viewportStack[index];
    _viewportY = _viewportStack[index + 1];
    _viewportWidth = _viewportStack[index + 2];
    _viewportHeight = _viewportStack[index + 3];
    // Do not call set viewport here, as this flips the y coordinate and multiplies by pixelRatio!
    //this.setViewport(_viewportX, _viewportY, _viewportWidth, _viewportHeight);

    _viewportStack.length = index;

    this._viewportChanged();
  };

  this._viewportChanged = function () {
    // MainPass needs to know when the viewport changes because there may
    // be ongoing render passes that need to be updated.
    _mainPass.viewportChanged();
  };

  this.updatePixelScale = function (pixelsPerUnit, camera) {
    this.getMainPass().updatePixelScale(pixelsPerUnit, camera);
  };

  /** @returns {TimingManager|undefined} */
  this.getTimingManager = function () {
    return _timingManager;
  };

  /** @returns {boolean} */
  this.hasGpuTime = function () {
    return !!_timingManager;
  };

  /** @returns {number} */
  this.gpuFrameTime = function () {
    if (_timingManager) {
      return _timingManager.totalFrameTime();
    } else {
      return 0;
    }
  };

  /** @returns {GPUCullingEngine|null} */
  this.getGPUCullingEngine = function () {
    return _gpuCullingEngine;
  };

  this.setFpsTarget = function (target) {
    _fpsTarget = target;

    // e.g. 10 submits per frame at 30fps.
    const TARGET_SUBMITS_PER_SECOND = 300;
    _targetSubmitsPerFrame = TARGET_SUBMITS_PER_SECOND / _fpsTarget;

    _vb.setFpsTarget(_fpsTarget);
  };

  /**
   * If progressive rendering is on, we limit the upload budget per frame.
   * @param {boolean} progressive - Whether progressive rendering is enabled
   */
  this.setProgressiveRendering = function (progressive) {
    _vb.setProgressiveRendering(progressive);
  };

  /** @returns {BufferManagerWebGPU|null} */
  this.getIndirectBufferManager = function () {
    return _indirectBufferManager;
  };

  /**
   * Refreshes all state that depends on the render target configuration.
   * Call this after changing the MRT setup, e.g., adding/removing ID-targets.
   */
  this.refreshRenderTargets = function () {
    _mainPass.resetPipelines();
    _postPass.resetPipelines();

    if (_canvas.width !== 0 && _canvas.height !== 0) {
      _mainPass.resize(_canvas.width, _canvas.height);
      _postPass.resize();
      _idResolvePass.resize();
      _msaaResolvePass.resize();
    }
    this.invalidateRenderBundles();
  };

  /**
   * Returns whether category highlighting is currently enabled.
   * @returns {boolean}
   */
  this.isCategoryHighlightingEnabled = function () {
    return _renderTargets.isCategoryHighlightingEnabled();
  };

  /**
   * Checks if category highlighting can be enabled based on device limits.
   * @returns {boolean} True if category highlighting is supported by the hardware.
   */
  this.isCategoryHighlightingSupported = function () {
    return _device?.limits.maxColorAttachmentBytesPerSample >= RequiredBytesPerSample;
  };

  /**
   * Returns true when category highlighting uses the fallback mode (packing category into upper 16 bits of modelId).
   * This happens when category highlighting is enabled but hardware doesn't support a 3rd ID target.
   * @returns {boolean}
   */
  this.isCategoryHighlightingFallback = function () {
    return this.isCategoryHighlightingEnabled() && !this.isCategoryHighlightingSupported();
  };

  /** @returns {boolean} */
  this.useWboit = function () {
    return USE_WBOIT;
  };
}

Renderer.Events = Events;